import { findCell, rowIsHeader, TableMap } from "@tiptap/pm/tables";

import type { CellAttrs, CellPosition, HeaderType } from "@/components/tiptap/tables/types";
import type { NodeWithPos } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import type { TableRect } from "@tiptap/pm/tables";
import type { CSSProperties } from "react";

export function getNearestCellCoords(rect: TableRect, rows: number = 0, cols: number = 0) {
  const x = Math.max(0, rect.left);
  const y = Math.max(0, rect.top);

  const col = Math.min(x, rect.map.width - cols - 1);
  const row = Math.min(y, rect.map.height - rows - 1);

  return { col, row };
}

export function updateRect(rect: TableRect, tr: Transaction) {
  const table = rect.tableStart ? tr.doc.nodeAt(rect.tableStart - 1) : tr.doc;

  if (!table) {
    return null;
  }

  rect.table = table;
  rect.map = TableMap.get(rect.table);

  return rect;
}

export function isCellLike(node: Node) {
  return node.type.name === "tableCell" || node.type.name === "tableHeader";
}

export function getCellPosition(doc: Node, pos: number): CellPosition | null {
  const cellPos = doc.resolve(pos);

  try {
    const { left: col, top: row } = findCell(cellPos);
    return {
      col,
      row,
      address: getCellAddress(row, col),
    };
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function getCellAddress(row: number, col: number) {
  let letter = "";
  let num = col + 1;

  while (num > 0) {
    const remainder = num % 26 || 26;
    letter = String.fromCharCode(remainder + 96) + letter;
    num = Math.floor((num - remainder) / 26);
  }

  return `${letter}${row + 1}`.toUpperCase();
}

export function getColumnFromCellPos(state: EditorState, cellPos: number) {
  const $cell = state.doc.resolve(cellPos);
  const table = $cell.node(-1);
  const tableStart = $cell.start(-1);
  const colIndex = TableMap.get(table).colCount(cellPos - tableStart);

  return getColumnCells(table, colIndex);
}

export function getHeaderCells(tableNode: Node, type: HeaderType) {
  const tableMap = TableMap.get(tableNode);

  switch (type) {
    case "row": {
      return getRowCells(tableNode, 0, ["tableHeader"]);
    }

    case "column": {
      const cells = getColumnCells(tableNode, 0, ["tableHeader"]);
      const headerRowIsEnabled = rowIsHeader(tableMap, tableNode, 0);

      return headerRowIsEnabled ? cells.slice(1) : cells;
    }

    default: {
      return [];
    }
  }
}

export function getColumnCells(
  tableNode: Node,
  col: number,
  types: string[] = ["tableHeader", "tableCell"],
): NodeWithPos[] {
  const cells: NodeWithPos[] = [];
  const map = TableMap.get(tableNode);

  for (let row = 0; row < map.height; row++) {
    const pos = map.map[col + row * map.width];
    const node = tableNode.nodeAt(pos);

    if (node && types.includes(node.type.name)) {
      cells.push({ node, pos });
    }
  }

  return cells;
}

function getRowCells(
  tableNode: Node,
  row: number,
  types: string[] = ["tableHeader", "tableCell"],
): NodeWithPos[] {
  const cells: NodeWithPos[] = [];
  const map = TableMap.get(tableNode);

  for (let col = 0; col < map.width; col++) {
    const pos = map.map[col + row * map.width];
    const node = tableNode.nodeAt(pos);

    if (node && types.includes(node.type.name)) {
      cells.push({ node, pos });
    }
  }

  return cells;
}

export function getTableStyleProps(table: Node, cellMinWidth: number = 0) {
  let totalWidth = 0;
  let fixedWidth = true;
  const firstRow = table.firstChild;
  const colStyles: CSSProperties[] = [];

  if (!firstRow) {
    return {};
  }

  firstRow.forEach((cell) => {
    const { colspan, colwidth } = cell.attrs as CellAttrs;

    Array.from({ length: colspan }).forEach((_, index) => {
      const columnWidth = colwidth?.[index];
      const colStyle = {
        width: columnWidth ? `${columnWidth}px` : "",
        minWidth: columnWidth ? "" : `${cellMinWidth}px`,
      };

      colStyles.push(colStyle);

      totalWidth += columnWidth || cellMinWidth;

      if (!columnWidth) {
        fixedWidth = false;
      }
    });
  });

  return {
    tableStyle: {
      width: fixedWidth ? `${totalWidth}px` : "",
      minWidth: fixedWidth ? "" : `${totalWidth}px`,
    },
    colStyles,
  };
}
